import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { KeyRound, Loader2, AlertTriangle } from 'lucide-react';
import { useSupportMode } from '@/contexts/SupportModeContext';
import { SupportAccessReasonDialog } from '@/components/support/SupportAccessReasonDialog';
import {
  describeShopSupportAccessError,
  listMyShopSupportAccess,
  requestShopSupportAccess,
  SCOPE_LABEL,
  type MyShopSupportAccessRow,
} from '@/lib/support/shopSupportAccess';

/**
 * Core-100 R5 CORE-BJ / OD-49 — the operator's side of shop-authorised support access.
 *
 * An operator does not browse customer shops. They ASK one shop (by its id, typically from a bug report or from the
 * shop on the phone) with a stated purpose; an owner or manager of that shop approves or denies it in the product;
 * only an approved, unexpired, unused approval can be opened. Every refusal comes from the server and is shown as
 * the server's reason.
 */
const STATUS_LABEL: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
  REQUESTED: { label: 'Waiting for the shop', variant: 'outline' },
  APPROVED: { label: 'Approved — not opened', variant: 'default' },
  USED: { label: 'Opened', variant: 'secondary' },
  EXPIRED: { label: 'Expired', variant: 'secondary' },
  DENIED: { label: 'Denied by the shop', variant: 'destructive' },
  REVOKED: { label: 'Revoked', variant: 'destructive' },
};

export const SupportShopAccessRequests = () => {
  const navigate = useNavigate();
  const { startGrantedSupportSession } = useSupportMode();
  const [rows, setRows] = useState<MyShopSupportAccessRow[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [shopId, setShopId] = useState('');
  const [purpose, setPurpose] = useState('');
  const [withNames, setWithNames] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [pending, setPending] = useState<MyShopSupportAccessRow | null>(null);

  const load = useCallback(async () => {
    try {
      setRows(await listMyShopSupportAccess());
      setLoadError(null);
    } catch (error) {
      // Never render "no requests" on a failed load.
      setLoadError(describeShopSupportAccessError(error as { message?: string }) ?? 'Your shop access requests could not be loaded.');
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const submit = async () => {
    setBusy(true);
    setNotice(null);
    try {
      const res = await requestShopSupportAccess(shopId.trim(), purpose.trim(),
        withNames ? 'OPERATIONAL_READ_WITH_CUSTOMER_NAMES' : 'OPERATIONAL_READ');
      setNotice({
        kind: 'ok',
        text: res.decision === 'already_requested'
          ? 'You already have an open request for this shop. It is waiting for the shop to decide.'
          : 'Request sent. An owner or manager of the shop must approve it before anything can be opened.',
      });
      setPurpose('');
      await load();
    } catch (error) {
      setNotice({ kind: 'error', text: describeShopSupportAccessError(error as { message?: string }) ?? 'The request could not be sent.' });
    } finally {
      setBusy(false);
    }
  };

  const open = async (disclosureReason: string) => {
    if (!pending) return;
    const res = await startGrantedSupportSession({
      grantId: pending.grant_id,
      disclosureReason,
      note: 'Opened from shop-authorised support access',
      returnPath: '/support/shops',
    });
    setPending(null);
    if (res.ok) {
      navigate('/support/granted-shop');
    } else {
      setNotice({ kind: 'error', text: res.message ?? 'Support access could not be opened.' });
      await load();
    }
  };

  return (
    <Card data-testid="support-shop-access-requests">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><KeyRound className="h-5 w-5" />Shop-authorised access</CardTitle>
        <CardDescription>
          Ask a shop for short, read-only access. The shop approves or denies it, can revoke it at any time, and sees
          every read. Approvals last at most one hour and are never extended.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-2">
          <Input placeholder="Shop id" value={shopId} onChange={(e) => setShopId(e.target.value)} aria-label="Shop id" />
          <Textarea placeholder="Why do you need access? The shop sees this." value={purpose}
            onChange={(e) => setPurpose(e.target.value)} aria-label="Purpose" />
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={withNames} onCheckedChange={(v) => setWithNames(v === true)} aria-label="Include customer names" />
            Also ask to see customer names (phone, email and address are never shown)
          </label>
          <Button onClick={submit} disabled={busy || !shopId.trim() || purpose.trim().length < 10}>
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Request access
          </Button>
        </div>

        {notice && (
          <Alert variant={notice.kind === 'error' ? 'destructive' : 'default'} data-testid="support-shop-access-notice">
            {notice.kind === 'error' && <AlertTriangle className="h-4 w-4" />}
            <AlertDescription>{notice.text}</AlertDescription>
          </Alert>
        )}

        {loadError ? (
          <Alert variant="destructive" data-testid="support-shop-access-load-error">
            <AlertDescription>{loadError}</AlertDescription>
          </Alert>
        ) : rows === null ? (
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">You have no shop access requests.</p>
        ) : (
          <div className="space-y-2">
            {rows.map((r) => {
              const st = STATUS_LABEL[r.status] ?? { label: r.status, variant: 'outline' as const };
              return (
                <div key={r.grant_id} className="p-3 rounded-md border border-border flex items-start justify-between gap-3"
                  data-testid={`support-shop-access-row-${r.grant_id}`}>
                  <div className="space-y-1 min-w-0">
                    <p className="text-sm font-medium truncate">{r.shop_name ?? `Shop ${r.shop_id}`}</p>
                    <p className="text-xs text-muted-foreground">{r.request_purpose}</p>
                    <p className="text-xs text-muted-foreground">{SCOPE_LABEL[r.granted_scope ?? r.requested_scope]}</p>
                    {r.access_expires_at && (
                      <p className="text-xs text-muted-foreground">Approval ends {new Date(r.access_expires_at).toLocaleString()}</p>
                    )}
                    {r.revoke_reason && <p className="text-xs text-destructive">{r.revoke_reason}</p>}
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <Badge variant={st.variant}>{st.label}</Badge>
                    {r.status === 'APPROVED' && (
                      <Button size="sm" onClick={() => setPending(r)}>Open read-only view</Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      <SupportAccessReasonDialog
        open={!!pending}
        onOpenChange={(next) => { if (!next) setPending(null); }}
        targetLabel={pending?.shop_name ?? undefined}
        onConfirm={open}
      />
    </Card>
  );
};
